import React, { useState, useCallback } from "react";
import {
  Loader2,
  FileText,
  Copy,
  Check,
  Trash2,
  ExternalLink,
  Pencil,
  Plus,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../api/api";
import MainLayout from "../sidebar/Sidebar.js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function ProposalsList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [location, setLocation] = useState("Proposals");
  const [copiedUrl, setCopiedUrl] = useState(null);

  // Fetch existing proposals
  const { data: proposalsData, isLoading: isLoadingProposals } = useQuery({
    queryKey: ["proposals"],
    queryFn: async () => {
      const response = await api.get("/proposals/");
      return response.data;
    },
  });

  // Delete proposal mutation
  const deleteProposalMutation = useMutation({
    mutationFn: async (proposalId) => {
      await api.delete(`/proposals/${proposalId}`);
      return proposalId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["proposals"]);
      toast.success("Proposal deleted");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to delete proposal");
    },
  });

  // Copy share URL to clipboard
  const copyToClipboard = useCallback((url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(id);
    setTimeout(() => setCopiedUrl(null), 2000);
  }, []);

  const proposals = proposalsData?.items || [];

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex-1 overflow-hidden py-6 px-5 min-h-0">
          <div className="h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 pb-6 flex-shrink-0">
              <div>
                <h1 className="text-xl font-semibold" style={{ color: "#1C1C1C" }}>
                  Proposals
                </h1>
                <p className="text-sm" style={{ color: "#727272" }}>
                  Manage your generated PDF proposals
                </p>
              </div>
              <Button
                onClick={() => navigate("/proposals/create")}
                style={{ backgroundColor: "#006de2", color: "white" }}
                className="rounded-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Proposal
              </Button>
            </div>

            {/* Proposals List */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {isLoadingProposals ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : proposals.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-lg mb-2">No proposals yet</p>
                  <p className="text-sm mb-4">Create your first proposal to get started</p>
                  <Button
                    onClick={() => navigate("/proposals/create")}
                    style={{ backgroundColor: "#006de2", color: "white" }}
                    className="rounded-lg"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Proposal
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {proposals.map((proposal) => (
                    <div
                      key={proposal.id}
                      className="bg-white rounded-xl p-4 flex items-center justify-between"
                      style={{ border: "1px solid #e8e8e8" }}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" style={{ color: "#006de2" }} />
                          <span className="font-medium" style={{ color: "#1C1C1C" }}>
                            {proposal.title || "Untitled Proposal"}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              proposal.status === "viewed"
                                ? "bg-green-100 text-green-700"
                                : proposal.status === "sent"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {proposal.status}
                          </span>
                        </div>
                        <div className="text-sm mt-1" style={{ color: "#727272" }}>
                          Created: {new Date(proposal.created_at).toLocaleDateString()}
                          {proposal.expires_at && (
                            <span className="ml-3">
                              Expires: {new Date(proposal.expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/proposals/edit/${proposal.id}`)}
                          title="Edit proposal"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(`${window.location.origin}/p/${proposal.share_token}`, proposal.id)}
                          title="Copy share link"
                        >
                          {copiedUrl === proposal.id ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <a
                          href={`/p/${proposal.share_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-blue-600 rounded transition-colors"
                          title="View PDF"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteProposalMutation.mutate(proposal.id)}
                          className="text-gray-400 hover:text-red-600"
                          title="Delete proposal"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
